import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, of, tap } from 'rxjs';
import { User, UserCreatePayload, UserEditPayload } from '@core/models/entities/user.models';
import { HttpApi } from './http.api';
import { WlcmPaginatedData, WlcmQueryParams } from '@wlcm/common';
import { ApiFeature } from '@core/models/api-feature.models';

const feature: ApiFeature = new ApiFeature('admin', 'admins');

@Injectable({ providedIn: 'root' })
export class UserApi {
  private readonly _user$: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);

  readonly user$: Observable<User | null> = this._user$.asObservable();

  constructor(private readonly httpApi: HttpApi) {}

  get user(): User | null {
    return this._user$.value;
  }

  set user(value: User | null) {
    this._user$.next(value);
  }

  getAdminProfile(): Observable<User | null> {
    return this.httpApi.get<User>(`admin/profile`).pipe(
      tap((user: User) => {
        this.user = user;
      }),
      catchError(error => {
        this.user = null;
        console.log('Error fetching admin profile:', error);
        return of(error);
      })
    );
  }

  load(params: WlcmQueryParams): Observable<WlcmPaginatedData<User>> {
    return this.httpApi.get(`${feature.many}/paginated`, { params });
  }

  add(data: UserCreatePayload): Observable<User> {
    return this.httpApi.post(`${feature.one}`, data);
  }

  delete(id: string): Observable<User> {
    return this.httpApi.delete(`${feature.one}/${id}`);
  }

  patch(id: string, data: UserEditPayload): Observable<User> {
    return this.httpApi.patch(`${feature.one}/${id}`, data);
  }

  sendInvite(id: string): Observable<User> {
    return this.httpApi.post(`${feature.one}/${id}/sendInvite`, {});
  }
}
